import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import HelpModal from '../HelpModal';
import { FaPlay, FaPause, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import HEXASLogo from '../assets/Logos/HEXASLogo.jpg';
import { useEffect } from 'react';
import { useTextHighlight } from '../utils/highlightUtils';

const Class17MatchingPeoplePractice01 = () => {
  const navigate = useNavigate();

  const highlightRef = useRef(null);
  useTextHighlight(highlightRef);

  const [showHelp, setShowHelp] = useState(false);

  const questionRefs = useRef([]);
  const rightColRef = useRef(null);
  const q36_40Ref = useRef(null);


  // Make sure your answers state exists
  const [answers, setAnswers] = useState(Array(20).fill(''));
  const [currentQuestion, setCurrentQuestion] = useState(14);
  const onNext = () => {
    setCurrentQuestion(prev => Math.min(prev + 1, 19));
  };

  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(100 * 60); // 30 minutes in seconds
  const [showSeconds, setShowSeconds] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 0) {
          clearInterval(timer);
          navigate('/');  // Redirect to home
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [navigate]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (showSeconds) {
      return `${mins} : ${secs.toString().padStart(2, '0')} remaining`;
    }
    return `${mins} minutes remaining`;
  };

  const handleStartTest = () => {
    navigate(`/guided-practice-01-form`);
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    setCurrentTime(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;
    setDuration(audioRef.current.duration);
  };

  const handleSeek = (e) => {
    if (!audioRef.current) return;
    const time = parseFloat(e.target.value);
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const handleVolumeChange = (e) => {
    if (!audioRef.current) return;
    const vol = parseFloat(e.target.value);
    setVolume(vol);
    audioRef.current.volume = vol;
    setIsMuted(vol === 0);
  };

  const handleMute = () => {
    if (!audioRef.current) return;
    if (isMuted) {
      audioRef.current.muted = false;
      setIsMuted(false);
      audioRef.current.volume = volume;
    } else {
      audioRef.current.muted = true;
      setIsMuted(true);
    }
  };

  const GreenCheck = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="inline-block"
    >
      <path
        d="M7 11L9 13.5L16 7"
        stroke="#7DD77D"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );


  return (
    <div className="min-h-screen bg-white flex flex-col font-[Arial]">
      {/* Help Modal */}
      <HelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
      />
      {/* Top Bar (fixed) */}
      <div className="sticky top-0 z-30 bg-white">
        <div className="relative h-[62px] bg-[#F4C3C3] border-b border-black flex items-center justify-between px-4">
          {/* Left + Center combined on left side */}
          <div className="flex flex-col justify-center space-y-0.5 pl-6">
            <span className="text-[14px] text-left text-black font-bold leading-tight">
              HEXA'S Education
            </span>
            <div className="flex items-center space-x-6 text-[14px] text-black font-medium">
              <span
                onMouseEnter={() => setShowSeconds(true)}
                onMouseLeave={() => setShowSeconds(false)}
                className="cursor-pointer"
              >
                {formatTime(timeRemaining)}
              </span>
            </div>
          </div>
          {/* Right: Icons and Finish Button */}
          <div className="flex items-center space-x-5">
            <button
              onClick={() => navigate('/')}
              className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
            >
              Back Home
            </button>
            <button
              onClick={() => setShowHelp(true)}
              className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
            >
              Finish test
            </button>
            <div className="flex items-center space-x-8 pl-2 pr-2">
              {/* WiFi Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-9 h-9 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M17.778 8.222c-4.296-4.296-11.26-4.296-15.556 0A1 1 0 010.808 6.808c5.076-5.077 13.308-5.077 18.384 0a1 1 0 01-1.414 1.414zM14.95 11.05a7 7 0 00-9.9 0 1 1 0 01-1.414-1.414 9 9 0 0112.728 0 1 1 0 01-1.414 1.414zM12.12 13.88a3 3 0 00-4.24 0 1 1 0 01-1.415-1.415 5 5 0 717.07 0 1 1 0 01-1.415 1.415zM9 16a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              {/* Notification Bell Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                </svg>
              </div>
              {/* Hamburger Menu Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              {/* Pen/Edit Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        {/* Title Bar (fixed under top bar) */}
        <div className="relative bg-[#F4F7FA] shadow-sm border border-black rounded-lg mx-12 mt-4 mb-0 sticky top-[32px] z-20 flex items-center justify-between">
          {/* Left: Part Title */}
          <div className="flex-1 px-4 py-2 text-left">
            <h2 className="text-[23px] text-black">Part 1</h2>
            <h1 className="text-[18px] text-black">HEXA'S Reading Course Book</h1>
          </div>
          {/* Center: Absolutely Positioned Logo */}
          <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <img
              src={HEXASLogo}
              alt="HEXA'S Logo"
              className="h-[50px] object-contain"
            />
          </div>
          {/* Right: Empty space for consistency */}
          <div className="flex items-center justify-end w-[300px] pr-4">
            {/* Empty space - no audio player for reading */}
          </div>
        </div>
      </div>
      {/* Main content (scrollable) */}
      <div className="flex-1 flex flex-col items-center justify-start w-full">
        <div ref={highlightRef}
          className="flex flex-row w-full overflow-hidden -mb-16 mt-2 px-12 pb-20"
          style={{ height: 'calc(100vh - 180px)' }}
        >
          {/* Left: Reading Passage */}
          <div className="bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 w-[49%] h-full max-h-full overflow-y-auto">
            <div className="mb-8">
              <h2 className="text-[16px] font-bold mb-4 text-center">Conquering Earth’s space junk problem</h2>
              <h3 className="text-[14px] font-semibold mb-4 text-center">Satellites, rocket shards and collision debris are creating major traffic risks in orbit around the planet. Researchers are working to reduce these threats</h3>

              <div className="text-[14px] space-y-4 text-left">
                <p>
                  <strong>A</strong><br />
                  Last year, commercial companies, military and civil departments and amateurs sent more than 400 satellites into orbit, over four times the yearly average in the previous decade. Numbers could rise even more sharply if leading space companies follow through on plans to deploy hundreds to thousands of large constellations of satellites to space in the next few years.
                </p>

                <p>
                  All that traffic can lead to disaster. Ten years ago, a US commercial Iridium satellite smashed into an inactive Russian communications satellite called Cosmos-2251, creating thousands of new pieces of space shrapnel that now threaten other satellites in low Earth orbit – the zone stretching up to 2,000 kilometres in altitude. Altogether, there are roughly 20,000 human-made objects in orbit, from working satellites to small rocket pieces. And satellite operators can’t steer away from every potential crash, because each move consumes time and fuel that could otherwise be used for the spacecraft’s main job.
                </p>

                <p>
                  <strong>B</strong><br />
                  Concern about space junk goes back to the beginning of the satellite era, but the number of objects in orbit is rising so rapidly that researchers are investigating new ways of attacking the problem. Several teams are trying to improve methods for assessing what is in orbit, so that satellite operators can work more efficiently in ever-more-crowded space. Some researchers are now starting to compile a massive data set that includes the best possible information on where everything is in orbit. Others are developing taxonomies of space – working on measuring properties such as the shape and size of an object, so that satellite operators know how much to worry about what’s coming their way.
                </p>

                <p>
                  The alternative, many say, is unthinkable. Just a few uncontrolled space crashes could generate enough debris to set off a runaway cascade of fragments, rendering near-Earth space unusable. ‘If we go on like this, we will reach a point of no return,’ says Carolin Frueh, an astrodynamical researcher at Purdue University in West Lafayette, Indiana.
                </p>

                <p>
                  <strong>C</strong><br />
                  Even as our ability to monitor space objects increases, so too does the total number of items in orbit. That means companies, governments and other players in space are collaborating in new ways to avoid a shared threat. International groups such as the Inter-Agency Space Debris Coordination Committee have developed guidelines on space sustainability. Those include inactivating satellites at the end of their useful life by venting pressurised materials or leftover fuel that might lead to explosions. The intergovernmental groups also advise lowering satellites deep enough into the atmosphere that they will burn up or disintegrate within 25 years. But so far, only about half of all missions have abided by this 25-year goal, says Holger Krag, head of the European Space Agency’s space-debris office in Darmstadt, Germany. Operators of the planned large constellations of satellites say they will be responsible stewards in their enterprises in space, but Krag worries that problems could increase, despite their best intentions. ‘What happens to those that fail or go bankrupt?’ he asks. They are probably not going to spend money to remove their satellites from space.’
                </p>

                <p>
                  <strong>D</strong><br />
                  In theory, given the vastness of space, satellite operators should have plenty of room for all these missions to fly safely without ever nearing another object. So some scientists are tackling the problem of space junk by trying to find out where all the debris is to a high degree of precision. That would alleviate the need for many of the unnecessary manoeuvres that are carried out to avoid potential collisions. ‘If you knew precisely where everything was, you would almost never have a problem,’ says Marlon Sorge, a space-debris specialist at the Aerospace Corporation in El Segundo, California.
                </p>

                <p>
                  <strong>E</strong><br />
                  The field is called space traffic management, because it’s similar to managing traffic on the roads or in the air. Think about a busy day at an airport, says Moriba Jah, an astrodynamicist at the University of Texas at Austin: planes line up in the sky, landing and taking off close to one another in a carefully choreographed routine. Air-traffic controllers know the location of the planes down to one metre in accuracy. The same can’t be said for space debris. Not all objects in orbit are known, and even those included in databases are not tracked consistently.
                </p>

                <p>
                  <strong>F</strong><br />
                  An additional problem is that there is no authoritative catalogue that accurately lists the orbits of all known space debris. Jah illustrates this with a web-based database that he has developed. It draws on several sources, such as catalogues maintained by the US and Russian governments, to visualise where objects are in space. When he types in an identifier for a particular space object, the database draws a purple line to designate its orbit. Only this doesn’t quite work for a number of objects, such as a Russian rocket body designated in the database as object number 32280. When Jah enters that number, the database draws two purple lines: the US and Russian sources contain two completely different orbits for the same object. Jah says that it is almost impossible to tell which is correct, unless a third source of information made it possible to cross-correlate.
                </p>

                <p>
                  Jah describes himself as a space environmentalist: ‘I want to make space a place that is safe to operate, that is free and useful for generations to come.’ Until that happens, he argues, the space community will continue devolving into a tragedy in which all spaceflight operators are polluting a common resource.
                </p>
              </div>
            </div>
          </div>





          {/* Right: Questions */}
          <div
            ref={rightColRef}
            className="w-[50%] flex flex-col items-start bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 h-full max-h-full overflow-y-auto"
          >
            {/* Questions 36-40 */}
            <div className="mb-10" ref={q36_40Ref} id="questions-36-40">
              <h3 className="text-[15px] font-bold mb-4 text-left">Questions 36–40</h3>
              <p className="text-[14px] mb-4 text-left">
                Look at the following statements (Questions 36–40) and the list of people below.<br />
                Match each statement with the correct person, <strong>A, B, C</strong> or <strong>D</strong>.<br /><br />
                <strong>NB:</strong> You may use any letter more than once.
              </p>

              <table className="w-full text-[13px] mb-4 border border-black table-fixed" style={{ tableLayout: 'fixed' }}>
                <thead>
                  <tr className="bg-blue-200">
                    <th
                      className="border border-black px-2 py-1 text-left break-words"
                      style={{ width: '65%' }}
                    >
                      Statement
                    </th>
                    {["A", "B", "C", "D"].map((code) => (
                      <th
                        key={code}
                        className="border border-black px-1 py-1 text-center"
                        style={{ width: '8%', padding: '4px 2px', whiteSpace: 'nowrap' }}
                      >
                        {code}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    "Knowing the exact location of space junk would help prevent any possible danger.",
                    "Space should be available to everyone and should be preserved for the future.",
                    "A recommendation regarding satellites is widely ignored.",
                    "There is conflicting information about where some satellites are in space.",
                    "There is a risk we will not be able to undo the damage that occurs in space.",
                  ].map((statement, rowIdx) => {
                    return (
                      <tr key={rowIdx} ref={(el) => (questionRefs.current[rowIdx + 35] = el)}>
                        <td
                          className="border border-black px-2 py-1 text-left align-top break-words"
                          style={{ wordWrap: 'break-word' }}
                        >
                          <span className="font-bold mr-2">{36 + rowIdx}</span>
                          {statement}
                        </td>
                        {["A", "B", "C", "D"].map((code) => {
                          const isSelected = answers[rowIdx + 35] === code;
                          return (
                            <td
                              key={code}
                              className={`border border-black text-center cursor-pointer transition-colors relative align-middle ${isSelected ? "bg-blue-50" : ""
                                }`}
                              style={{ padding: 0, width: '8%', minWidth: '40px' }}
                              onClick={() => {
                                const newAnswers = [...answers];
                                newAnswers[rowIdx + 35] = code;
                                setAnswers(newAnswers);
                              }}
                            >
                              <span
                                className="flex items-center justify-center"
                                style={{ width: '100%', height: 36, minHeight: 36 }}
                              >
                                {isSelected ? <GreenCheck /> : null}
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Reference Table for People */}
              <div className="flex justify-start mt-4">
                <table className="border border-black text-[14px] bg-white">
                  <thead>
                    <tr>
                      <th className="border border-black px-4 py-1 text-center font-bold" colSpan={2}>
                        List of People
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { code: "A", name: "Carolin Frueh" },
                      { code: "B", name: "Holger Krag" },
                      { code: "C", name: "Marlon Sorge" },
                      { code: "D", name: "Moriba Jah" },
                    ].map((person) => (
                      <tr key={person.code}>
                        <td className="border border-black px-4 py-1 text-center font-bold w-12">{person.code}</td>
                        <td className="border border-black px-4 py-1 text-left">{person.name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>









        </div>
      </div>

      {/* Bottom Navigation (fixed footer) */}
      <div className="sticky bottom-0 left-0 w-full bg-[#D7DFEC] border-t border-gray-300 z-30 flex items-center justify-between px-4 py-3">
        {/* Left: Question Buttons */}
        <div className="flex items-center space-x-1">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((questionNumber) => (
            <button
              key={questionNumber}
              onClick={() => setCurrentQuestion(questionNumber)}
              className={`w-7 h-7 mx-0.5 rounded text-[12px] font-bold flex items-center justify-center ${currentQuestion === questionNumber
                ? 'bg-blue-500 text-white'
                : 'bg-black text-white'
                }`}
            >
              {questionNumber}
            </button>
          ))}
        </div>

        {/* Right: Arrows */}
        <div className="flex items-center space-x-2">
          {/* Backward Arrow */}
          <button
            onClick={() => {
              setCurrentQuestion((prev) => Math.max(1, prev - 1));
            }}
            className={`w-7 h-7 flex items-center justify-center rounded-full ${currentQuestion === 1 ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500'
              } text-white`}
            title="Previous Question"
            disabled={currentQuestion === 1}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Forward Arrow */}
          <button
            onClick={() => {
              setCurrentQuestion((prev) => Math.min(10, prev + 1));
            }}
            className={`w-7 h-7 flex items-center justify-center rounded-full ${currentQuestion === 10 ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500'
              } text-white`}
            title="Next Question"
            disabled={currentQuestion === 10}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>







    </div>
  );
};

export default Class17MatchingPeoplePractice01;
